import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  FileDown,
  LayoutGrid,
  BarChart3,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AppShell from "@/components/layout/AppShell";
import { leadsApi, insightsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";

import HeaderStats from "@/components/crm/HeaderStats";
import PipelineForecast from "@/components/crm/PipelineForecast";
import PrioritiesSection from "@/components/crm/PrioritiesSection";
import Circuit24hSection from "@/components/crm/Circuit24hSection";
import TodoTodayPanel from "@/components/crm/TodoTodayPanel";
import ScheduledTasksPanel from "@/components/crm/ScheduledTasksPanel";
import KanbanBoard from "@/components/crm/KanbanBoard";
import ListView from "@/components/crm/ListView";
import ViewToggle from "@/components/crm/ViewToggle";
import AnalyticsSection from "@/components/crm/AnalyticsSection";
import SalesKPIsSection from "@/components/crm/SalesKPIsSection";
import NextActionsSection from "@/components/crm/NextActionsSection";
import MorningBriefSection from "@/components/crm/MorningBriefSection";
import RecentActivitiesSection from "@/components/crm/RecentActivitiesSection";
import Filters from "@/components/crm/Filters";
import AddLeadDialog from "@/components/crm/AddLeadDialog";
import LeadDetailDialog from "@/components/crm/LeadDetailDialog";
import ImportDialog from "@/components/crm/ImportDialog";
import GoogleSheetsDialog from "@/components/crm/GoogleSheetsDialog";
import { isPrioritaire } from "@/lib/crmHelpers";

const DASHBOARD_TAB_KEY = "crm-dashboard-tab";

export default function Dashboard() {
  const { activeProjectId, activeProject, loading: settingsLoading } = useSettings();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [insightsMap, setInsightsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("all");
  const [prioriteFilter, setPrioriteFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("crm-view") || "kanban");
  const [tab, setTab] = useState(() => localStorage.getItem(DASHBOARD_TAB_KEY) || "overview");
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [googleOpen, setGoogleOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    localStorage.setItem("crm-view", view);
  }, [view]);

  useEffect(() => {
    localStorage.setItem(DASHBOARD_TAB_KEY, tab);
  }, [tab]);

  const loadAll = useCallback(async () => {
    try {
      const [ldata, sdata, idata] = await Promise.all([
        leadsApi.list(activeProjectId),
        leadsApi.stats(activeProjectId),
        insightsApi.forProject(activeProjectId).catch(() => ({})),
      ]);
      // Defensive: API might return non-array under transient errors / proxy
      // misbehaviour (e.g. HTML 500 page). Coerce so .filter / .map below
      // can never crash the whole tree.
      setLeads(Array.isArray(ldata) ? ldata : []);
      setStats(sdata && typeof sdata === "object" ? sdata : null);
      setInsightsMap(idata && typeof idata === "object" ? idata : {});
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des leads");
    } finally {
      setLoading(false);
    }
  }, [activeProjectId]);

  useEffect(() => {
    if (settingsLoading) return;
    (async () => {
      try {
        await leadsApi.seed();
      } catch (_) {}
      await loadAll();
    })();
  }, [loadAll, settingsLoading]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google")) setGoogleOpen(true);
  }, []);

  // Re-fetch when the tab/window becomes visible again. Without this, the
  // Pipeline KPIs go stale as soon as the user creates a lead from another
  // page (Leads, Inbox…) and comes back. (BUG-12)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") loadAll();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [loadAll]);

  useEffect(() => {
    if (!selectedLead) return;
    const updated = leads.find((l) => l.id === selectedLead.id);
    if (updated && updated !== selectedLead) setSelectedLead(updated);
  }, [leads, selectedLead]);

  const handleCreate = async (payload) => {
    const created = await leadsApi.create(payload);
    // Toast text: previously read "ajouté · Programme 20j lead généré" with
    // "lead" appearing twice. Rephrased so the project-duration suffix only
    // shows when an active project was actually attached.
    const duree = activeProject?.duree_jours;
    toast.success(
      duree
        ? `Lead « ${created.nom} » créé · plan d'action ${duree} jours généré`
        : `Lead « ${created.nom} » créé`
    );
    await loadAll();
  };
  const handleUpdate = async (id, payload) => {
    const updated = await leadsApi.update(id, payload);
    setSelectedLead(updated);
    await loadAll();
    return updated;
  };
  const handleDelete = async (id) => {
    await leadsApi.remove(id);
    setSelectedLead(null);
    toast.success("Lead supprimé");
    await loadAll();
  };
  const handleAction = async (id, type, description) => {
    const updated = await leadsApi.addAction(id, { type, description });
    if (selectedLead?.id === id) setSelectedLead(updated);
    await loadAll();
    return updated;
  };
  const handleProcedureToggle = async (id, key, value) => {
    const updated = await leadsApi.updateProcedure(id, key, value);
    if (selectedLead?.id === id) setSelectedLead(updated);
    await loadAll();
  };
  const handleProgrammeToggle = async (id, jour, index, fait) => {
    const updated = await leadsApi.updateProgramme(id, jour, index, fait);
    if (selectedLead?.id === id) setSelectedLead(updated);
    await loadAll();
    toast.success(fait ? "Étape marquée faite" : "Étape réouverte");
  };
  const handleStatutChange = async (id, statut) => {
    await handleUpdate(id, { statut });
    toast.success("Statut mis à jour");
  };

  const exportCsv = async () => {
    try {
      const params = activeProjectId ? `?projet_id=${activeProjectId}` : "";
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/leads/export.csv${params}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `sogi-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error("Erreur d'export");
    }
  };

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (q) {
        const hay = `${l.nom} ${l.telephone} ${l.email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statutFilter !== "all" && l.statut !== statutFilter) return false;
      if (prioriteFilter !== "all" && l.priorite !== prioriteFilter) return false;
      if (dateFilter === "today") {
        if (!l.prochaine_action) return false;
        if (l.prochaine_action.slice(0, 10) !== new Date().toISOString().slice(0, 10)) return false;
      } else if (dateFilter === "week") {
        if (!l.prochaine_action) return false;
        const d = new Date(l.prochaine_action);
        const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
        if (diff < 0 || diff > 7) return false;
      }
      return true;
    });
  }, [leads, search, statutFilter, prioriteFilter, dateFilter]);

  const priorityLeads = useMemo(
    () => leads.filter(isPrioritaire).slice(0, 12),
    [leads]
  );

  const dataMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          data-testid="data-menu-btn"
          className="hidden sm:inline-flex gap-2"
        >
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
          Données
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[220px]">
        <DropdownMenuItem onClick={() => setImportOpen(true)} data-testid="menu-import-csv" className="gap-2">
          <Upload className="h-4 w-4" />
          Importer un CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setGoogleOpen(true)} data-testid="menu-google-sheets" className="gap-2">
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
          Google Sheets
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportCsv} data-testid="menu-export-csv" className="gap-2">
          <FileDown className="h-4 w-4" />
          Exporter le CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <AppShell
      topbarProps={{
        search,
        onSearchChange: setSearch,
        onAddLead: () => setAddOpen(true),
        onRefresh: loadAll,
        rightExtra: dataMenu,
      }}
    >
      <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        {/* Page header */}
        <div className="mb-5 flex flex-col gap-1 sm:mb-6">
          <p className="text-label text-muted-foreground">Pilotage commercial</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            Tableau de bord
          </h1>
          <p className="text-sm text-muted-foreground">
            {activeProject?.nom
              ? `Projet actif · ${activeProject.nom} · Programme ${activeProject.duree_jours}j`
              : "Vue consolidée de tous les projets"}
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="grid w-full grid-cols-3 max-w-md mb-6"
            data-testid="dashboard-tabs"
          >
            <TabsTrigger value="overview" data-testid="tab-overview" className="gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Vue d'ensemble</span>
              <span className="sm:hidden">Vue</span>
            </TabsTrigger>
            <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="gap-1.5">
              <LayoutGrid className="h-3.5 w-3.5" />
              Pipeline
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* === Overview === */}
          <TabsContent value="overview" className="space-y-6 animate-fade-in">
            <HeaderStats stats={stats} loading={loading} />
            <TodoTodayPanel projetId={activeProjectId} />
            <ScheduledTasksPanel />
            <MorningBriefSection />
            <NextActionsSection />
            <Circuit24hSection
              leads={leads}
              onOpenLead={setSelectedLead}
              onAction={handleAction}
              onProcedureToggle={handleProcedureToggle}
            />
            <PrioritiesSection
              leads={priorityLeads}
              onOpenLead={setSelectedLead}
              onAction={handleAction}
              onStatutChange={handleStatutChange}
            />
          </TabsContent>

          {/* === Pipeline === */}
          <TabsContent value="pipeline" className="space-y-4 animate-fade-in">
            <PipelineForecast leads={leads} insightsMap={insightsMap} />

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-label text-muted-foreground">Pipeline</div>
                <h2 className="font-heading mt-0.5 text-xl font-semibold tracking-tight">
                  {view === "kanban" ? "Vue Kanban" : "Vue Liste"}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    · {filteredLeads.length} lead{filteredLeads.length > 1 ? "s" : ""}
                  </span>
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <ViewToggle view={view} onChange={setView} />
                <Filters
                  search={search}
                  onSearchChange={setSearch}
                  statutFilter={statutFilter}
                  onStatutChange={setStatutFilter}
                  prioriteFilter={prioriteFilter}
                  onPrioriteChange={setPrioriteFilter}
                  dateFilter={dateFilter}
                  onDateChange={setDateFilter}
                />
              </div>
            </div>

            {view === "kanban" ? (
              <KanbanBoard
                leads={filteredLeads}
                onOpenLead={setSelectedLead}
                onStatutChange={handleStatutChange}
                loading={loading}
              />
            ) : (
              <ListView leads={filteredLeads} />
            )}
          </TabsContent>

          {/* === Analytics === */}
          <TabsContent value="analytics" className="space-y-6 animate-fade-in">
            <SalesKPIsSection />
            <AnalyticsSection stats={stats} />
            <RecentActivitiesSection />
          </TabsContent>
        </Tabs>
      </div>

      <AddLeadDialog open={addOpen} onOpenChange={setAddOpen} onCreate={handleCreate} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={loadAll} />
      <GoogleSheetsDialog open={googleOpen} onOpenChange={setGoogleOpen} onImported={loadAll} />
      <LeadDetailDialog
        lead={selectedLead}
        onOpenChange={(o) => !o && setSelectedLead(null)}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onAction={handleAction}
        onProcedureToggle={handleProcedureToggle}
        onProgrammeToggle={handleProgrammeToggle}
      />
    </AppShell>
  );
}
